import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';

interface RegionCardProps {
  region: {
    id: string;
    name: string;
    language: string;
    image: string;
    alt: string;
    storiesCount: number;
    creatorsCount: number;
    popularGenres: string[];
    culturalHighlight: string;
  };
}

export default function RegionCard({ region }: RegionCardProps) {
  return (
    <div className="group bg-card rounded-xl overflow-hidden shadow-warm hover:shadow-warm-lg transition-all duration-300 hover:-translate-y-1">
      <div className="relative h-48 overflow-hidden">
        <AppImage
          src={region.image}
          alt={region.alt}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background/90 via-background/40 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4">
          <h3 className="text-xl font-heading font-bold text-primary-foreground mb-1">
            {region.name}
          </h3>
          <p className="text-sm text-primary-foreground/90 font-medium">
            {region.language}
          </p>
        </div>
      </div>

      <div className="p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="BookOpenIcon" size={18} className="text-primary" />
              <span className="text-sm font-medium">{region.storiesCount.toLocaleString('en-IN')} Stories</span>
            </div>
            <div className="flex items-center space-x-2 text-text-secondary">
              <Icon name="UserGroupIcon" size={18} className="text-secondary" />
              <span className="text-sm font-medium">{region.creatorsCount} Creators</span>
            </div>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-sm text-muted-foreground mb-2 font-medium">Popular Genres:</p>
          <div className="flex flex-wrap gap-2">
            {region.popularGenres.map((genre, index) => (
              <span
                key={index}
                className="px-3 py-1 bg-muted text-text-primary text-xs font-medium rounded-full"
              >
                {genre}
              </span>
            ))}
          </div>
        </div>

        <div className="mb-4 p-3 bg-accent/10 rounded-lg border border-accent/20">
          <p className="text-sm text-text-primary">
            <Icon name="SparklesIcon" size={16} className="inline mr-2 text-accent" />
            {region.culturalHighlight}
          </p>
        </div>

        <Link
          href={`/regional-hubs/${region.id}`}
          className="flex items-center justify-center space-x-2 w-full px-4 py-3 bg-primary text-primary-foreground rounded-lg font-cta font-bold transition-all duration-300 hover:bg-primary-dark hover:shadow-warm"
        >
          <span>Explore {region.name}</span>
          <Icon name="ArrowRightIcon" size={20} />
        </Link>
      </div>
    </div>
  );
}